'use client';

import { createContext, useContext, ReactNode, useState, useCallback } from 'react';
import { PageProps } from 'libs/contentful/queries/page/props';
import { ResolvedVisualSettings } from 'utils/pageHelpers/visualSettings';
import { CustomQueryStringParameterItems } from 'libs/contentful/types';

interface PageContextType {
  page?: PageProps;
  visualSettings?: ResolvedVisualSettings;
  updateVisualSettings?: (settings: Partial<ResolvedVisualSettings>) => void;
  /** Client preview override for custom QSPs; when set, used instead of server-resolved/baseline */
  resolvedCustomQueryStringParametersOverride?: CustomQueryStringParameterItems[] | null;
  updateResolvedCustomQueryStringParameters?: (items: CustomQueryStringParameterItems[] | null) => void;
}

const PageContext = createContext<PageContextType>({});

/**
 * Context provider for page-level data.
 * Provides a centralized way to access page-level data within page components.
 *
 * @example
 * <PageProvider page={page} visualSettings={visualSettings}>
 *   <Component />
 * </PageProvider>
 */
export const PageProvider = ({
  children,
  page,
  visualSettings: initialVisualSettings,
}: {
  children: ReactNode;
  page?: PageProps;
  visualSettings?: ResolvedVisualSettings;
}) => {
  const [visualSettings, setVisualSettings] = useState<ResolvedVisualSettings | undefined>(initialVisualSettings);
  const [resolvedCustomQueryStringParametersOverride, setResolvedCustomQueryStringParametersOverride] = useState<
    CustomQueryStringParameterItems[] | null | undefined
  >(undefined);

  const updateVisualSettings = useCallback((settings: Partial<ResolvedVisualSettings>) => {
    setVisualSettings(prev => {
      if (!prev) {
        return undefined;
      }
      return { ...prev, ...settings };
    });
  }, []);

  const updateResolvedCustomQueryStringParameters = useCallback((items: CustomQueryStringParameterItems[] | null) => {
    setResolvedCustomQueryStringParametersOverride(items);
  }, []);

  return (
    <PageContext.Provider
      value={{
        page,
        visualSettings,
        updateVisualSettings,
        resolvedCustomQueryStringParametersOverride,
        updateResolvedCustomQueryStringParameters,
      }}>
      {children}
    </PageContext.Provider>
  );
};

/**
 * Hook to access page-level data.
 *
 * @example
 * const Component = () => {
 *   const { page } = usePage();
 *   return <div>{page?.title}</div>;
 * }
 */
export const usePage = () => {
  const context = useContext(PageContext);
  return context;
};
